<template>
  <!-- ====================================================================
    LOGIN PAGE — Orchit Design System
    Full-screen mesh gradient background + centered glassmorphism card
    Color scheme: aligned with https://www.orchit.ai/
  ===================================================================== -->
  <div class="orchit-login-root orchit-page">

    <!-- Decorative floating orbs (background) -->
    <div class="orchit-orb orchit-orb--1" aria-hidden="true"></div>
    <div class="orchit-orb orchit-orb--2" aria-hidden="true"></div>
    <div class="orchit-orb orchit-orb--3" aria-hidden="true"></div>

    <!-- Centered login card -->
    <div class="orchit-login-wrapper">

      <!-- ── Card ── -->
      <div class="orchit-login-card orchit-animate-fade-up">

        <!-- ── Brand header ── -->
        <div class="orchit-login-brand orchit-animate-fade-up orchit-animate-delay-1">
          <img
            src="../../../assets/images/logo/orchit-logo.png"
            alt="Orchit CMS"
            class="orchit-login-logo"
          />
          <div class="orchit-badge orchit-animate-fade-up orchit-animate-delay-2">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <circle cx="5" cy="5" r="5" fill="currentColor" />
            </svg>
            CMS
          </div>
        </div>

        <!-- ── Headline (Logic Step: Login) ── -->
        <div v-if="loginStep === 'login'" class="orchit-login-headline orchit-animate-fade-up orchit-animate-delay-2">
          <h1 class="orchit-login-title">Welcome back</h1>
          <p class="orchit-login-subtitle">Sign in to your workspace to continue</p>
        </div>

        <!-- ── Headline (Logic Step: OTP) ── -->
        <div v-else-if="loginStep === 'otp' || loginStep === 'reset_otp'" class="orchit-login-headline orchit-animate-fade-up orchit-animate-delay-2">
          <h1 class="orchit-login-title">Verify it's you</h1>
          <p v-if="loginStep === 'otp'" class="orchit-login-subtitle">We sent a 6-digit code to <strong class="orchit-gradient-text">{{ email }}</strong></p>
          <p v-else class="orchit-login-subtitle">Enter the verification code sent to your email</p>
        </div>

        <!-- ── Headline (Logic Step: Forgot Password) ── -->
        <div v-else-if="loginStep === 'forgot_password'" class="orchit-login-headline orchit-animate-fade-up orchit-animate-delay-2">
          <h1 class="orchit-login-title">Reset password</h1>
          <p class="orchit-login-subtitle">Enter your email and we'll send you a link to reset your password.</p>
        </div>

        <!-- ── Headline (Logic Step: New Password) ── -->
        <div v-else-if="loginStep === 'new_password'" class="orchit-login-headline orchit-animate-fade-up orchit-animate-delay-2">
          <h1 class="orchit-login-title">New password</h1>
          <p class="orchit-login-subtitle">Please create a unique password that you don't use anywhere else.</p>
        </div>

        <!-- ── Headline (Logic Step: Success) ── -->
        <div v-else-if="loginStep === 'reset_success'" class="orchit-login-headline orchit-animate-fade-up orchit-animate-delay-2">
          <div class="orchit-success-icon-wrap">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M20 6L9 17L4 12" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h1 class="orchit-login-title">Password reset</h1>
          <p class="orchit-login-subtitle">Your password has been successfully reset. Click below to log in magically.</p>
        </div>

        <!-- ── Error alert ── -->
        <div
          v-if="errorMessage"
          class="orchit-alert-error orchit-animate-fade-up"
          role="alert"
          aria-live="polite"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
            <path d="M8 5v3.5M8 11h.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          {{ errorMessage }}
        </div>

        <!-- ── Login Form ── -->
        <form
          v-if="loginStep === 'login'"
          @submit.prevent="handleLogin"
          autocomplete="off"
          novalidate
          class="orchit-login-form"
        >
          <!-- Email -->
          <div
            class="orchit-field orchit-animate-fade-up orchit-animate-delay-2"
            :class="{ 'orchit-field--error': emailError }"
          >
            <label for="login-email" class="orchit-label">Email address</label>
            <div class="orchit-input-wrap">
              <span class="orchit-input-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="1" y="3" width="14" height="10" rx="2" stroke="currentColor" stroke-width="1.4"/>
                  <path d="M1 5.5l6.293 4.293a1 1 0 001.414 0L15 5.5" stroke="currentColor" stroke-width="1.4"/>
                </svg>
              </span>
              <input
                v-model="email"
                type="email"
                id="login-email"
                class="orchit-input"
                :class="{ 'is-error': emailError }"
                placeholder="you@company.com"
                @blur="touched.email = true"
                autocomplete="off"
                aria-describedby="email-error"
              />
            </div>
            <p v-if="emailError" id="email-error" class="orchit-error" role="alert">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <circle cx="6" cy="6" r="5" stroke="currentColor" stroke-width="1.2"/>
                <path d="M6 4v2.5M6 8.5h.01" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
              </svg>
              {{ emailError }}
            </p>
          </div>

          <!-- Password -->
          <div
            v-if="loginMethod === 'password'"
            class="orchit-field orchit-animate-fade-up orchit-animate-delay-3"
            :class="{ 'orchit-field--error': passwordError }"
          >
            <div class="orchit-field-row">
              <label for="login-password" class="orchit-label">Password</label>
              <a href="javascript:void(0)" class="orchit-forgot-link" @click="openForgotPassword" tabindex="-1">Forgot password?</a>
            </div>
            <div class="orchit-input-wrap">
              <span class="orchit-input-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="3" y="7" width="10" height="8" rx="1.5" stroke="currentColor" stroke-width="1.4"/>
                  <path d="M5 7V5a3 3 0 016 0v2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
                </svg>
              </span>
              <input
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                id="login-password"
                class="orchit-input orchit-input--password"
                :class="{ 'is-error': passwordError }"
                placeholder="Enter your password"
                @blur="touched.password = true"
                autocomplete="off"
                aria-describedby="password-error"
              />
              <button
                type="button"
                class="orchit-toggle-pw"
                @click="showPassword = !showPassword"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                tabindex="-1"
              >
                <!-- Eye icon (show) -->
                <svg v-if="!showPassword" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" stroke-width="1.4"/>
                  <circle cx="8" cy="8" r="2" stroke="currentColor" stroke-width="1.4"/>
                </svg>
                <!-- Eye-off icon (hide) -->
                <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M13.5 3L2.5 13M6.3 6.4A2 2 0 009.6 9.7M3.7 4.9C2.3 6 1 8 1 8s2.5 5 7 5c1.3 0 2.5-.4 3.6-1M6.2 2.6C6.8 2.2 7.4 3 8 3c4.5 0 7 5 7 5s-.7 1.4-1.9 2.7" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            <p v-if="passwordError" id="password-error" class="orchit-error" role="alert">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <circle cx="6" cy="6" r="5" stroke="currentColor" stroke-width="1.2"/>
                <path d="M6 4v2.5M6 8.5h.01" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
              </svg>
              {{ passwordError }}
            </p>
          </div>

          <!-- Submit -->
          <button
            id="login-submit-btn"
            type="submit"
            class="orchit-btn-primary orchit-login-submit orchit-animate-fade-up orchit-animate-delay-4"
            :disabled="isLoading"
          >
            <span v-if="isLoading" class="orchit-spinner" aria-hidden="true"></span>
            <span v-if="!isLoading">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style="margin-right:6px" aria-hidden="true">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            {{ isLoading ? (loginMethod === 'password' ? 'Signing in...' : 'Sending OTP...') : (loginMethod === 'password' ? 'Sign in' : 'Send OTP') }}
          </button>

          <!-- Toggle Login Method -->
          <div class="orchit-method-toggle orchit-animate-fade-up orchit-animate-delay-5">
            <button
              type="button"
              class="orchit-toggle-btn"
              @click="loginMethod = loginMethod === 'password' ? 'otp' : 'password'"
            >
              {{ loginMethod === 'password' ? 'Sign in with OTP instead' : 'Sign in with Password instead' }}
            </button>
          </div>
        </form>

        <!-- ── OTP Form ── -->
        <div v-else-if="loginStep === 'otp' || loginStep === 'reset_otp'" class="orchit-otp-container orchit-animate-fade-up orchit-animate-delay-2">
          <div class="orchit-otp-grid">
            <input
              v-for="(digit, index) in otpDigits"
              :key="index"
              ref="otpInputs"
              v-model="otpDigits[index]"
              type="text"
              maxlength="1"
              class="orchit-otp-input"
              @input="handleOtpInput(index, $event)"
              @keydown="handleOtpKeyDown(index, $event)"
              autocomplete="one-time-code"
              :disabled="isLoading"
            />
          </div>

          <div class="orchit-otp-resend">
            <p v-if="cooldown > 0" class="orchit-timer">
              Resend code in <span>{{ cooldown }}s</span>
            </p>
            <button
              v-else
              type="button"
              class="orchit-resend-btn"
              @click="resendOtp"
            >
              Didn't receive code? <span>Resend</span>
            </button>
          </div>

          <button
            @click="loginStep === 'otp' ? handleOtpSubmit() : handleResetOtpVerify()"
            class="orchit-btn-primary orchit-login-submit"
            :disabled="isLoading || otpDigits.join('').length < 6"
          >
            <span v-if="isLoading" class="orchit-spinner" aria-hidden="true"></span>
            {{ isLoading ? 'Verifying...' : 'Verify and continue' }}
          </button>

          <div class="orchit-back-to-login-wrap">
            <button
              type="button"
              class="orchit-back-link"
              @click="backToLogin"
              :disabled="isLoading"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M19 12H5M12 19l-7-7 7-7" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Return to Sign In
            </button>
          </div>
        </div>

        <!-- ── Forgot Password Form ── -->
        <form v-else-if="loginStep === 'forgot_password'" @submit.prevent="handleResetRequest" class="orchit-login-form orchit-animate-fade-up">
          <div class="orchit-field" :class="{ 'orchit-field--error': emailError }">
            <label for="reset-email" class="orchit-label">Email address</label>
            <div class="orchit-input-wrap">
              <span class="orchit-input-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="3" width="14" height="10" rx="2" stroke="currentColor" stroke-width="1.4"/><path d="M1 5.5l6.293 4.293a1 1 0 001.414 0L15 5.5" stroke="currentColor" stroke-width="1.4"/></svg>
              </span>
              <input v-model="email" type="email" id="reset-email" class="orchit-input" placeholder="you@company.com" @blur="touched.email = true" />
            </div>
            <p v-if="emailError" class="orchit-error">{{ emailError }}</p>
          </div>

          <button type="submit" class="orchit-btn-primary orchit-login-submit" :disabled="isLoading">
            <span v-if="isLoading" class="orchit-spinner"></span>
            {{ isLoading ? 'Sending...' : 'Send reset instructions' }}
          </button>

          <div class="orchit-back-to-login-wrap">
            <button type="button" class="orchit-back-link" @click="backToLogin">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M19 12H5M12 19l-7-7 7-7" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Return to Sign In
            </button>
          </div>
        </form>

        <!-- ── New Password Form ── -->
        <form v-else-if="loginStep === 'new_password'" @submit.prevent="handleNewPasswordSubmit" class="orchit-login-form orchit-animate-fade-up">
          <div class="orchit-field" :class="{ 'orchit-field--error': newPasswordError }">
            <label class="orchit-label">New password</label>
            <div class="orchit-input-wrap">
              <span class="orchit-input-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="3" y="7" width="10" height="8" rx="1.5" stroke="currentColor" stroke-width="1.4"/><path d="M5 7V5a3 3 0 016 0v2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
              </span>
              <input v-model="newPassword" :type="showNewPassword ? 'text' : 'password'" class="orchit-input" placeholder="Min 6 characters" @blur="newPasswordTouched = true" />
              <button type="button" class="orchit-toggle-pw" @click="showNewPassword = !showNewPassword">
                <svg v-if="!showNewPassword" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" stroke-width="1.4"/><circle cx="8" cy="8" r="2" stroke="currentColor" stroke-width="1.4"/></svg>
                <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13.5 3L2.5 13M6.3 6.4A2 2 0 009.6 9.7M3.7 4.9C2.3 6 1 8 1 8s2.5 5 7 5c1.3 0 2.5-.4 3.6-1M6.2 2.6C6.8 2.2 7.4 3 8 3c4.5 0 7 5 7 5s-.7 1.4-1.9 2.7" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
              </button>
            </div>
            <p v-if="newPasswordError" class="orchit-error">{{ newPasswordError }}</p>
          </div>

          <div class="orchit-field" :class="{ 'orchit-field--error': confirmPasswordError }">
            <label class="orchit-label">Confirm password</label>
            <div class="orchit-input-wrap">
              <span class="orchit-input-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="3" y="7" width="10" height="8" rx="1.5" stroke="currentColor" stroke-width="1.4"/><path d="M5 7V5a3 3 0 016 0v2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
              </span>
              <input v-model="confirmPassword" :type="showConfirmPassword ? 'text' : 'password'" class="orchit-input" placeholder="Repeat password" @blur="confirmPasswordTouched = true" />
              <button type="button" class="orchit-toggle-pw" @click="showConfirmPassword = !showConfirmPassword">
                <svg v-if="!showConfirmPassword" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" stroke-width="1.4"/><circle cx="8" cy="8" r="2" stroke="currentColor" stroke-width="1.4"/></svg>
                <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13.5 3L2.5 13M6.3 6.4A2 2 0 009.6 9.7M3.7 4.9C2.3 6 1 8 1 8s2.5 5 7 5c1.3 0 2.5-.4 3.6-1M6.2 2.6C6.8 2.2 7.4 3 8 3c4.5 0 7 5 7 5s-.7 1.4-1.9 2.7" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
              </button>
            </div>
            <p v-if="confirmPasswordError" class="orchit-error">{{ confirmPasswordError }}</p>
          </div>

          <button type="submit" class="orchit-btn-primary orchit-login-submit" :disabled="isLoading">
            <span v-if="isLoading" class="orchit-spinner"></span>
            {{ isLoading ? 'Updating...' : 'Update password' }}
          </button>

          <div class="orchit-back-to-login-wrap">
            <button type="button" class="orchit-back-link" @click="backToLogin">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M19 12H5M12 19l-7-7 7-7" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Return to Sign In
            </button>
          </div>
        </form>

        <!-- ── Reset Success ── -->
        <div v-else-if="loginStep === 'reset_success'" class="orchit-login-form orchit-animate-fade-up" style="text-align:center">
          <button @click="backToLogin" class="orchit-btn-primary orchit-login-submit">
            All set! Back to login
          </button>
        </div>

        <!-- ── Footer note ── -->
        <p class="orchit-login-footer orchit-animate-fade-up orchit-animate-delay-5">
          © {{ new Date().getFullYear() }}
          <span class="orchit-gradient-text" style="font-weight:700">Orchit CMS</span>
        </p>
      </div>

      <!-- ── Right panel — visual brand side ── -->
      <div class="orchit-login-panel orchit-animate-fade-up orchit-animate-delay-1" aria-hidden="true">
        <div class="orchit-panel-inner">
          <!-- Decorative inner orbs -->
          <div class="orchit-panel-orb orchit-panel-orb--1"></div>
          <div class="orchit-panel-orb orchit-panel-orb--2"></div>

          <div class="orchit-panel-content">
            <!-- Icon / Logo -->
            <div class="orchit-panel-icon-wrap">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" class="orchit-panel-icon">
                <rect width="48" height="48" rx="14" fill="rgba(255,255,255,0.15)"/>
                <path d="M24 12L36 32H12L24 12Z" fill="white" opacity="0.9"/>
                <circle cx="24" cy="34" r="3" fill="white" opacity="0.7"/>
              </svg>
            </div>

            <h2 class="orchit-panel-title">
              Orchestrate Your<br/>Projects with AI
            </h2>
            <p class="orchit-panel-text">
              From planning to delivery — manage your entire workflow with intelligent tools built for modern teams.
            </p>

            <!-- Feature chips -->
            <div class="orchit-panel-chips">
              <span class="orchit-panel-chip">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                Real-time collaboration
              </span>
              <span class="orchit-panel-chip">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                AI-powered insights
              </span>
              <span class="orchit-panel-chip">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                Role-based access
              </span>
              <span class="orchit-panel-chip">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                360° reporting
              </span>
            </div>
          </div>

          <!-- Bottom tagline -->
          <p class="orchit-panel-tagline">© {{ new Date().getFullYear() }} Orchit CMS. All rights reserved.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";
import apiService from "@/services/apiService";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "@/store/auth";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";

// ── State ─────────────────────────────────────────────────────────────────
const email       = ref("");
const password    = ref("");
const errorMessage= ref("");
const isLoading   = ref(false);
const showPassword= ref(false);
const loginMethod = ref<'password' | 'otp'>('password');
const loginStep   = ref<'login' | 'otp' | 'forgot_password' | 'reset_otp' | 'new_password' | 'reset_success'>('login');

const otpDigits   = ref(['', '', '', '', '', '']);
const cooldown    = ref(0);
let timerInterval: any = null;

const newPassword        = ref("");
const confirmPassword    = ref("");
const showNewPassword    = ref(false);
const showConfirmPassword= ref(false);
const newPasswordTouched = ref(false);
const confirmPasswordTouched = ref(false);

const router    = useRouter();
const route     = useRoute();
const authStore = useAuthStore();

const touched = ref({ email: false, password: false });
const otpInputs = ref<HTMLInputElement[]>([]);

// ── Lifecycle ──────────────────────────────────────────────────────────────
onMounted(() => {
  if (route.query.expired === "true") {
    toast.error("Your session has expired. Please log in again.", {
      autoClose: 5000,
      position: toast.POSITION.TOP_CENTER,
    });
    router.replace({ path: "/login", query: { ...route.query, expired: undefined } });
  }
});

// ── Validation ────────────────────────────────────────────────────────────
const emailError = computed(() => {
  if (!touched.value.email) return "";
  if (!email.value.trim()) return "Email address is required";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) return "Please enter a valid email";
  return "";
});

const passwordError = computed(() => {
  if (!touched.value.password) return "";
  if (!password.value) return "Password is required";
  if (password.value.length < 6) return "Password must be at least 6 characters";
  return "";
});

const isFormValid = computed(() => {
  if (loginMethod.value === 'otp') return !emailError.value && email.value;
  return !emailError.value && !passwordError.value && email.value && password.value;
});

// ── Login ──────────────────────────────────────────────────────────────────
async function handleLogin() {
  errorMessage.value  = "";
  touched.value.email   = true;
  if (loginMethod.value === 'password') touched.value.password = true;

  if (!isFormValid.value) return;

  isLoading.value = true;
  
  if (loginMethod.value === 'password') {
    // Password Login: Use the real API
    try {
      const payload  = { email: email.value, password: password.value };
      const response = await apiService.post("cms/login", payload);

      // Handle Password Expired if returned in success body
      if (response?.password_expired) {
        toast.warning("Your password has expired. Please reset it to continue.");
        handleResetRequest(true); // Call real API to send OTP
        return;
      }

      const token =
        response?.token || response?.data?.token || response?.data?.access_token;

      if (token) {
        toast.success("Welcome back! Signing you in…");
        localStorage.setItem("token", token);
        authStore.setToken(token);

        const user = response?.data?.user || response?.user;
        if (user) {
          authStore.setUser({
            id:         user._id,
            name:       user.name,
            email:      user.email,
            roleTitle:  user.role_id?.title || "User",
            profilePic: user.profile_pic || "",
          });

          if (user.role_id) {
            const { usePermissionsStore } = await import("@/store/permissions");
            const permissionsStore = usePermissionsStore();
            permissionsStore.setPermissions(
              user.role_id.title || "",
              user.role_id.permissions || []
            );
          }
        }

        let redirect = (router.currentRoute.value.query.redirect as string) || "/";
        if (redirect.includes("/login") || redirect.includes("/auth/login")) redirect = "/";
        if (!redirect.startsWith("/")) redirect = "/" + redirect;

        await nextTick();
        try {
          await router.push(redirect);
          setTimeout(() => {
            if (router.currentRoute.value.path === "/login") {
              window.location.href = redirect;
            }
          }, 500);
        } catch {
          window.location.href = redirect;
        }
      } else {
        toast.error("Login failed — no token received");
      }
    } catch (error: any) {
      // Handle Password Expired if returned in error response
      if (error?.response?.data?.password_expired) {
        toast.warning("Your password has expired. Please reset it to continue.");
        handleResetRequest(true);
        return;
      }
      toast.error("Invalid credentials. Please try again.");
      errorMessage.value =
        error?.response?.data?.message || "Login failed. Please check your credentials.";
    } finally {
      isLoading.value = false;
    }
  } else {
    // OTP Login: Real API call
    try {
      await apiService.sendLoginOTP(email.value);
      loginStep.value = 'otp';
      startCooldown();
      toast.info("OTP sent to your email!");
      
      // Auto focus first OTP input
      nextTick(() => {
        otpInputs.value[0]?.focus();
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to send OTP. Please check your email.");
      errorMessage.value = error?.response?.data?.message || "Failed to send OTP.";
    } finally {
      isLoading.value = false;
    }
  }
}

// ── OTP Logic ─────────────────────────────────────────────────────────────
function startCooldown() {
  cooldown.value = 30;
  if (timerInterval) clearInterval(timerInterval);
  timerInterval = setInterval(() => {
    if (cooldown.value > 0) cooldown.value--;
    else clearInterval(timerInterval);
  }, 1000);
}

function handleOtpInput(index: number, event: any) {
  const value = event.target.value;
  // Only allow numbers
  if (!/^\d*$/.test(value)) {
    otpDigits.value[index] = "";
    return;
  }

  if (value && index < 5) {
    otpInputs.value[index + 1]?.focus();
  }
}

function handleOtpKeyDown(index: number, event: KeyboardEvent) {
  if (event.key === "Backspace" && !otpDigits.value[index] && index > 0) {
    otpInputs.value[index - 1]?.focus();
  }
}

async function handleOtpSubmit() {
  const otp = otpDigits.value.join("");
  if (otp.length < 6) {
    toast.error("Please enter the full 6-digit code");
    return;
  }

  isLoading.value = true;
  try {
    const response = await apiService.verifyLoginOTP(email.value, otp);
    
    // Process successful OTP login
    const token = response?.token || response?.data?.token || response?.data?.access_token;
    if (token) {
      toast.success("OTP Verified! Signing you in...");
      localStorage.setItem("token", token);
      authStore.setToken(token);

      const user = response?.data?.user || response?.user;
      if (user) {
        authStore.setUser({
          id:         user._id,
          name:       user.name,
          email:      user.email,
          roleTitle:  user.role_id?.title || "User",
          profilePic: user.profile_pic || "",
        });

        if (user.role_id) {
          const { usePermissionsStore } = await import("@/store/permissions");
          const permissionsStore = usePermissionsStore();
          permissionsStore.setPermissions(
            user.role_id.title || "",
            user.role_id.permissions || []
          );
        }
      }

      let redirect = (router.currentRoute.value.query.redirect as string) || "/";
      if (redirect.includes("/login") || redirect.includes("/auth/login")) redirect = "/";
      if (!redirect.startsWith("/")) redirect = "/" + redirect;

      await nextTick();
      try {
        await router.push(redirect);
        setTimeout(() => {
          if (router.currentRoute.value.path === "/login") {
            window.location.href = redirect;
          }
        }, 500);
      } catch {
        window.location.href = redirect;
      }
    } else {
      toast.error("Verification successful but no token received");
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message || "Invalid or expired OTP");
    errorMessage.value = error?.response?.data?.message || "Verification failed";
  } finally {
    isLoading.value = false;
  }
}

async function resendOtp() {
  if (cooldown.value > 0) return;
  isLoading.value = true;
  try {
    await apiService.sendLoginOTP(email.value);
    startCooldown();
    toast.info("A new OTP has been sent!");
  } catch (error: any) {
    toast.error("Failed to resend code");
  } finally {
    isLoading.value = false;
  }
}

function backToLogin() {
  loginStep.value = 'login';
  errorMessage.value = "";
}

// ── Reset Password Logic ──────────────────────────────────────────────────
function openForgotPassword() {
  loginStep.value = 'forgot_password';
  errorMessage.value = "";
}

async function handleResetRequest(autoTrigger = false) {
  if (!autoTrigger) {
    touched.value.email = true;
    if (emailError.value) return;
  }

  isLoading.value = true;
  try {
    await apiService.post("cms/request-password-otp", { email: email.value });
    isLoading.value = false;
    loginStep.value = 'reset_otp';
    startCooldown();
    toast.info("Verification code sent to your email!");
    nextTick(() => { otpInputs.value[0]?.focus(); });
  } catch (error: any) {
    isLoading.value = false;
    toast.error(error?.response?.data?.message || "Failed to send reset code");
  }
}

function handleResetOtpVerify() {
  const otp = otpDigits.value.join("");
  if (otp.length < 6) return toast.error("Please enter the 6-digit code");
  
  // Transition to new password screen (API call happens at the end)
  loginStep.value = 'new_password';
}

const newPasswordError = computed(() => {
  if (!newPasswordTouched.value) return "";
  if (!newPassword.value) return "New password is required";
  if (newPassword.value.length < 6) return "Password must be at least 6 characters";
  return "";
});

const confirmPasswordError = computed(() => {
  if (!confirmPasswordTouched.value) return "";
  if (confirmPassword.value !== newPassword.value) return "Passwords do not match";
  return "";
});

async function handleNewPasswordSubmit() {
  newPasswordTouched.value = true;
  confirmPasswordTouched.value = true;
  
  if (newPasswordError.value || confirmPasswordError.value || !newPassword.value) return;

  isLoading.value = true;
  try {
    const payload = {
      email: email.value,
      otp: otpDigits.value.join(""),
      new_password: newPassword.value
    };
    await apiService.post("cms/reset-password", payload);
    
    isLoading.value = false;
    loginStep.value = 'reset_success';
    toast.success("Password updated successfully!");
  } catch (error: any) {
    isLoading.value = false;
    toast.error(error?.response?.data?.message || "Failed to reset password");
  }
}

onUnmounted(() => {
  if (timerInterval) clearInterval(timerInterval);
});


// Clear top-level error on any input change
watch([email, password], () => {
  if (errorMessage.value) errorMessage.value = "";
});
</script>

<style scoped lang="scss">
/* ── Variables & Keyframes ── */
$orchit-primary: #7C3AED;
$orchit-accent: #06B6D4;
$orchit-text: #111827;

/* ── Root & Layout ── */
.orchit-login-root {
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  overflow: hidden;
  background: var(--orchit-gradient-mesh);
}

.orchit-login-wrapper {
  position: relative;
  z-index: 10;
  display: flex;
  width: 100%;
  max-width: 1100px;
  background: rgba(255, 255, 255, 0.45);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 32px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.1), 0 8px 32px rgba(124, 58, 237, 0.05);
  overflow: hidden;
}

/* ── Decorative Background Orbs ── */
.orchit-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  z-index: 1;
  opacity: 0.4;
  animation: orchit-float-orb 20s infinite alternate ease-in-out;

  &--1 { width: 400px; height: 400px; background: rgba(124, 58, 237, 0.2); top: -100px; right: -100px; }
  &--2 { width: 350px; height: 350px; background: rgba(6, 182, 212, 0.15); bottom: -50px; left: -100px; animation-delay: -5s; }
  &--3 { width: 250px; height: 250px; background: rgba(167, 139, 250, 0.1); top: 30%; left: 10%; animation-delay: -10s; }
}

/* ── Left Side (Form Area) ── */
.orchit-login-card {
  flex: 1;
  padding: 4rem 3.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  max-width: 480px;

  @media (max-width: 900px) {
    max-width: 100%;
    padding: 3rem 2rem;
  }
}

.orchit-login-brand {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 3rem;

  .orchit-login-logo {
    height: 42px;
    filter: drop-shadow(0 4px 12px rgba(124, 58, 237, 0.25));
  }

  .orchit-badge {
    padding: 0.45rem 1rem;
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    color: var(--orchit-primary);
    background: var(--orchit-primary-light);
    border-radius: 100px;
    border: 1.5px solid rgba(124, 58, 237, 0.15);
    display: flex;
    align-items: center;
    gap: 6px;
  }
}

.orchit-login-headline {
  margin-bottom: 2.5rem;

  .orchit-login-title {
    font-size: 2.25rem;
    font-weight: 800;
    color: var(--orchit-text-primary);
    margin-bottom: 0.5rem;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #111827 0%, #374151 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .orchit-login-subtitle {
    font-size: 1.05rem;
    color: var(--orchit-text-secondary);
  }
}

/* ── Form Styling ── */
.orchit-login-form {
  .orchit-field {
    margin-bottom: 1.5rem;

    .orchit-label {
      color: #1a1a1a !important; /* Slightly darker for better accessibility */
      margin-bottom: 0.75rem !important;
    }

    /* Target the container of label and link */
    .orchit-field-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0px !important; /* Managed by label margin */
    }
  }

  .orchit-input-wrap {
    position: relative;
    display: flex;
    align-items: center;

    .orchit-input-icon {
      position: absolute;
      left: 14px;
      color: var(--orchit-text-muted);
      display: flex;
      transition: var(--orchit-transition);
    }

    .orchit-input {
      padding-left: 42px;
      padding-right: 14px;
      height: 52px;
      background: rgba(255, 255, 255, 0.9);
      border: 1.5px solid rgba(124, 58, 237, 0.1);
      font-size: 0.95rem;

      &::placeholder {
        color: #888888; /* Slightly darker placeholder */
      }

      &:focus {
        background: #FFFFFF;
        border-color: var(--orchit-primary);
        box-shadow: 0 0 0 4px rgba(124, 58, 237, 0.12);

        & + .orchit-input-icon {
          color: var(--orchit-primary);
        }
      }

      &--password { padding-right: 48px; }

      &.is-error {
        border-color: #EF4444 !important;
        background: #FEF2F2 !important;
      }
    }

    .orchit-toggle-pw {
      position: absolute;
      right: 12px;
      background: none;
      border: none;
      padding: 6px;
      color: var(--orchit-text-muted);
      cursor: pointer;
      border-radius: 8px;
      display: flex;
      transition: var(--orchit-transition);

      &:hover {
        background: rgba(124, 58, 237, 0.08);
        color: var(--orchit-primary);
      }
    }
  }

  .orchit-forgot-link {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--orchit-primary);
    text-decoration: none;
    transition: var(--orchit-transition);

    &:hover { color: var(--orchit-primary-dark); text-decoration: underline; }
  }

  .orchit-login-submit {
    width: 100%;
    height: 54px;
    font-size: 1rem;
    margin-top: 1rem;
    border-radius: 14px;
    background: var(--orchit-gradient-brand);
    box-shadow: 0 12px 24px rgba(124, 58, 237, 0.3);

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 16px 32px rgba(124, 58, 237, 0.4);
    }

    &:disabled { opacity: 0.7; cursor: wait; }
  }
}

.orchit-alert-error {
  background: #FEF2F2;
  border-left: 4px solid #EF4444;
  padding: 1rem;
  color: #B91C1C;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 2rem;
}

.orchit-spinner {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: orchit-spin-slow 0.8s linear infinite;
  margin-right: 10px;
}

.orchit-login-footer {
  margin-top: auto;
  padding-top: 3rem;
  font-size: 0.85rem;
  color: var(--orchit-text-muted);
  text-align: center;
}

/* ── Right Panel (Visual Area) ── */
.orchit-login-panel {
  flex: 1.2;
  position: relative;
  background: #111827;
  overflow: hidden;
  padding: 4rem;
  color: white;
  display: flex;
  flex-direction: column;

  @media (max-width: 900px) { display: none; }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 70% 30%, rgba(124, 58, 237, 0.3) 0%, transparent 60%);
    pointer-events: none;
  }
}

.orchit-panel-inner {
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.orchit-panel-content {
  margin-top: auto;
  margin-bottom: auto;

  .orchit-panel-icon-wrap {
    margin-bottom: 2rem;
  }

  .orchit-panel-title {
    font-size: 3.25rem;
    font-weight: 800;
    line-height: 1.1;
    letter-spacing: -0.04em;
    margin-bottom: 1.5rem;
    color: #FFFFFF !important;
    text-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  }

  .orchit-panel-text {
    font-size: 1.15rem;
    color: rgba(255, 255, 255, 0.95) !important;
    max-width: 440px;
    margin-bottom: 2.5rem;
    line-height: 1.6;
    font-weight: 500;
  }
}

.orchit-panel-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  .orchit-panel-chip {
    padding: 0.6rem 1rem;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 100px;
    font-size: 0.85rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    backdrop-filter: blur(10px);
    transition: var(--orchit-transition);

    &:hover { background: rgba(255, 255, 255, 0.15); transform: translateY(-2px); }

    svg { color: var(--orchit-accent); }
  }
}

.orchit-panel-tagline {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6) !important;
  margin-top: auto;
  font-weight: 500;
}

.orchit-panel-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  z-index: 1;

  &--1 { width: 400px; height: 400px; background: rgba(124, 58, 237, 0.25); top: -200px; right: -200px; }
  &--2 { width: 300px; height: 300px; background: rgba(6, 182, 212, 0.2); bottom: -150px; left: -100px; }
}

/* ── OTP Specific Styling ── */
.orchit-otp-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.orchit-otp-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin-bottom: 0.5rem;

  .orchit-otp-input {
    width: 100%;
    aspect-ratio: 1;
    text-align: center;
    font-size: 1.5rem;
    font-weight: 800;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.9);
    border: 1.5px solid rgba(124, 58, 237, 0.1);
    color: var(--orchit-primary);
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:focus {
      outline: none;
      background: #FFFFFF;
      border-color: var(--orchit-primary);
      box-shadow: 0 0 0 4px rgba(124, 58, 237, 0.15);
      transform: scale(1.05);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.orchit-otp-resend {
  text-align: center;
  font-size: 0.9rem;
  font-weight: 500;

  .orchit-timer {
    color: var(--orchit-text-muted);
    span { color: var(--orchit-primary); font-weight: 700; }
  }

  .orchit-resend-btn {
    background: none;
    border: none;
    color: var(--orchit-text-muted);
    cursor: pointer;
    font-weight: 500;
    transition: var(--orchit-transition);

    span {
      color: var(--orchit-primary);
      font-weight: 700;
      text-decoration: underline;
      text-underline-offset: 4px;
    }

    &:hover span { color: var(--orchit-primary-dark); }
  }
}

.orchit-back-to-login-wrap {
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
}

.orchit-back-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(124, 58, 237, 0.05);
  border: 1px solid rgba(124, 58, 237, 0.1);
  color: var(--orchit-text-muted);
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  padding: 0.6rem 1.25rem;
  border-radius: 100px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--orchit-primary-light);
    color: var(--orchit-primary);
    border-color: rgba(124, 58, 237, 0.2);
    transform: translateX(-4px);
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: translateX(-2px);
  }
}

.orchit-method-toggle {
  margin-top: 1.5rem;
  text-align: center;

  .orchit-toggle-btn {
    background: none;
    border: none;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--orchit-primary);
    cursor: pointer;
    padding: 0.5rem 1rem;
    border-radius: 100px;
    transition: var(--orchit-transition);

    &:hover {
      background: var(--orchit-primary-light);
      transform: translateY(-1px);
    }
  }
}

/* ── Success Specific Styling ── */
.orchit-success-icon-wrap {
  width: 72px;
  height: 72px;
  background: var(--orchit-primary-light);
  color: var(--orchit-primary);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  box-shadow: 0 12px 24px rgba(124, 58, 237, 0.15);
  animation: orchit-pulse-primary 2s infinite;
}

@keyframes orchit-pulse-primary {
  0% { box-shadow: 0 0 0 0 rgba(124, 58, 237, 0.4); }
  70% { box-shadow: 0 0 0 15px rgba(124, 58, 237, 0); }
  100% { box-shadow: 0 0 0 0 rgba(124, 58, 237, 0); }
}
</style>

